import { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import ArrowRightIcon from 'components/ui/icons/ArrowRight';
import useDeviceType from 'providers/DeviceType/hooks';

import { HorizontalListOptionRef, HorizontalListOptionType, List } from '../_components';

import styles from './Horizontal.module.scss';

type HorizontalProps = {
	className?: string;
	name: string;
	label?: string | JSX.Element;
	value?: string;
	error?: string;
	onChange: (option: string, name: string) => void;
	options: HorizontalListOptionType[];
};

export const Horizontal: React.FC<HorizontalProps> = ({
	className,
	options,
	onChange,
	error,
	label,
	name,
	value,
}) => {
	const { isMobile } = useDeviceType();

	const [isOpen, setIsOpen] = useState(false);

	const isInited = useRef(false);
	const valueElement = useRef<null | HTMLDivElement>(null);
	const elementRef = useRef<null | HTMLDivElement>(null);
	const listRef = useRef<null | HorizontalListOptionRef>(null);

	const activeIndex = useMemo(() => {
		if (!value) return null;

		return options.findIndex((i) => i.value === value);
	}, [value, options]);

	const isRightDisabled = useMemo(
		() => !(activeIndex !== null && activeIndex < options.length - 1),
		[activeIndex, options.length]
	);

	const isLeftDisabled = useMemo(
		() => !(activeIndex !== null && activeIndex > 0),
		[activeIndex, options.length]
	);

	const onLeftClick = (isDisabled: boolean) => {
		if (isDisabled && activeIndex !== null) {
			onChange(options[activeIndex - 1].value, name);
		}
	};

	const onRightClick = (isDisabled: boolean) => {
		if (isDisabled && activeIndex !== null) {
			onChange(options[activeIndex + 1].value, name);
		}
	};

	const onClickOutside = (event: MouseEvent) => {
		if (valueElement.current && !valueElement.current.contains(event.target as Node)) {
			setTimeout(setIsOpen.bind(null, false), 100);
		}
	};

	const onSelect = (option: string, name: string) => {
		if (isMobile) setIsOpen(false);
		onChange(option, name);
	};

	useEffect(() => {
		if (activeIndex && !isInited.current) {
			setTimeout(() => (isInited.current = true), 300);
		}
	}, [activeIndex]);

	useEffect(() => {
		listRef.current?.cleanSearch();

		if (isOpen) {
			if (!isMobile) {
				document.addEventListener('mousedown', onClickOutside);
				document.addEventListener('keydown', (e) => e.key === 'Escape' && setIsOpen(false));
			}
		} else {
			if (!isMobile) {
				document.removeEventListener('mousedown', onClickOutside);
				document.removeEventListener('keydown', (e) => e.key === 'Escape' && setIsOpen(false));
			}
		}

		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('keydown', (e) => e.key === 'Escape' && setIsOpen(false));
		};
	}, [isOpen]);

	return (
		<div ref={elementRef} className={classNames(styles.root, className)}>
			{typeof label === 'string' && <div className={styles.label}>{label}</div>}
			{typeof label === 'object' && label}
			<div className={styles.body}>
				<div
					className={classNames(styles.button, isLeftDisabled && styles.isDisabled)}
					onClick={onLeftClick.bind(null, !isLeftDisabled)}>
					<ArrowRightIcon />
				</div>
				<div ref={valueElement} className={styles.value} onClick={setIsOpen.bind(null, !isOpen)}>
					<div
						className={classNames(styles.options, isInited.current && styles.isInited)}
						style={{
							width: `${100 * options.length}%`,
							left: `-${100 * (activeIndex || 0)}%`,
						}}>
						{options.map((i) => (
							<div className={styles.item} key={i.value} style={{ width: '100%' }}>
								{i.label}
							</div>
						))}
					</div>
				</div>
				<div
					className={classNames(
						styles.button,
						styles.rightButton,
						isRightDisabled && styles.isDisabled
					)}
					onClick={onRightClick.bind(null, !isRightDisabled)}>
					<ArrowRightIcon />
				</div>
			</div>
			{error && <div className={styles.error}>{error}</div>}
			<List
				ref={listRef}
				options={options}
				name={name}
				label={label}
				isOpen={isOpen}
				onChangeOpenState={setIsOpen}
				onChange={onSelect}
			/>
		</div>
	);
};
